import type {
  DeskPhone,
  DeskPhoneHistoryRow,
  DeskPhones,
  MyDeskPhoneSession
} from "services/dialstack";

// Every sentence the hot-desk surfaces show (ENG-2293), in one place: the
// softphone picker, the Settings card, the walk-up prompt, the history page
// and the snackbars all say the same things about the same phone. The verb is
// "log in" / "log out" everywhere, because that is what the phone itself says
// (`*11` / `*12`) and what the DialStack portal shows ("Logged in").

export const LOG_IN_MENU_LABEL = "Log in on a desk phone…";
export const logOutMenuLabel = (name: string) => `Log out of ${name}`;
export const PICKER_TITLE = "Log in on a desk phone";
export const PICKER_HINT = "Choose the phone at your desk.";
export const BACK_LABEL = "Back to the keypad";
export const YOURS_LABEL = "Yours";
export const YOU_LABEL = "You";
export const NOBODYS_LABEL = "Nobody's";
export const NO_PHONES_LABEL = "This practice has no desk phones set up yet.";
export const PHONES_UNAVAILABLE_LABEL =
  "Desk phones can't be listed right now.";
export const LOADING_PHONES_LABEL = "Loading desk phones…";

export const LOG_IN_LABEL = "Log in";
export const LOG_IN_ANYWAY_LABEL = "Log in anyway";
export const LOG_IN_AS_ME_LABEL = "Log in as me";
export const LOG_OUT_LABEL = "Log out";
export const CANCEL_LABEL = "Cancel";
export const TRY_AGAIN_LABEL = "Try again";
export const BACK_TO_LIST_LABEL = "Back to the list";
export const NOT_NOW_LABEL = "Not now";
export const LOG_IN_AGAIN_LABEL = "Log in again";

export const LOGGING_IN_LABEL = "Logging in…";
export const LOGGING_OUT_LABEL = "Logging out…";
export const DONE_BODY =
  "Your calls ring there now; the phone picks up the change in a moment.";
export const CHANGED_BODY = "Check it and try again.";
export const FAILED_FALLBACK = "Try again, or ask an admin.";
export const DECT_HANDSET_NOTE =
  "This is a cordless handset: logging in briefly interrupts calls on the handsets that share its base.";
// The backend ends every login at 03:00 in the practice's time zone
// (`reset_desk_phone_sessions`); DialStack's own timer is only a backstop.
export const RESET_TIME_LABEL = "3 AM";
export const UNTIL_NOTE = `Until you log out, or ${RESET_TIME_LABEL}.`;
export const KEYPAD_HINT =
  "At any desk phone you can also dial *11, then your extension and voicemail PIN.";
export const THIS_COMPUTER_LABEL = "this computer";

export const logInTitle = (name: string) => `Log in on ${name}?`;
export const loggingInTitle = (name: string) => `Logging in on ${name}…`;
export const doneTitle = (name: string) => `${name} is yours.`;
export const changedTitle = (name: string) => `${name} changed just now.`;
export const failedTitle = (name: string) => `Couldn't log in on ${name}.`;
export const logOutTitle = (name: string) => `Log out of ${name}?`;
export const logOutFailedTitle = (name: string) =>
  `Couldn't log out of ${name}.`;

// Snackbars.
export const loggedInSnackbar = (name: string) =>
  `You're logged in on ${name}.`;
export const loggedOutSnackbar = (name: string, ringsOn: string) =>
  `You're logged out of ${name}. Your calls ring on ${ringsOn}.`;
export const phoneTakenSnackbar = (name: string, by: string) =>
  `${by} logged in on your phone, ${name}. Your calls ring on this computer until they log out.`;
export const phoneBackSnackbar = (name: string) => `${name} is yours again.`;
export function loginEndedSnackbar(
  name: string,
  reason: string,
  by: string
): string {
  switch (reason) {
    case "expired":
      return `Your login on ${name} ended.`;
    case "replaced":
      return `${by || "A colleague"} logged in on ${name}; you're logged out.`;
    case "reset":
      return `Your login on ${name} ended overnight.`;
    case "logged_out":
      return `You're logged out of ${name}.`;
    default:
      return `Your login on ${name} ended.`;
  }
}
// An expiry is the one ending worth offering to undo in a click.
export const loginEndedOffersRetry = (reason: string) => reason === "expired";

// "Jane Doe's", "Chris's" — the apostrophe form people write, not the
// typographic rule.
export const possessive = (name: string) => `${name}'s`;

export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

// "Jane Doe's and Max Kim's" — each name gets its own apostrophe.
export const possessiveNames = (names: string[]) =>
  joinNames(names.map(possessive));

export const shortTime = (iso: string | null) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const ownerNames = (phone: DeskPhone) =>
  phone.owners.map((owner) => owner.name);

// The second line of a picker row: whose the phone is, in two layers — the
// permanent owner, then who is logged in on it — in words.
export function holdersLabel(phone: DeskPhone): string {
  const parts: string[] = [];
  const owners = ownerNames(phone);
  if (phone.own) parts.push(YOURS_LABEL);
  else if (owners.length === 1) parts.push(possessive(owners[0]));
  else if (owners.length > 1) parts.push(`Shared by ${joinNames(owners)}`);
  else if (!phone.session) parts.push(NOBODYS_LABEL);
  if (phone.session) {
    const since = shortTime(phone.session.started_at);
    const who = phone.mine ? YOU_LABEL : phone.session.name;
    parts.push(
      `${who} logged in${since ? ` since ${since}` : ""}`.replace(
        /^You logged in/,
        "You're logged in"
      )
    );
  }
  let label = parts.join(" · ");
  if (phone.on_call) label += " · on a call";
  if (phone.online === false) label += " · offline";
  return label;
}

export const rowTitle = (phone: DeskPhone) =>
  `${phone.name}${phone.location ? ` · ${phone.location}` : ""}`;

// What a row does when activated, for assistive tech: the verb, then whose
// the phone is. A phone my calls already ring on is shown but not actionable.
export const rowLabel = (phone: DeskPhone) =>
  phone.mine
    ? `${rowTitle(phone)}. ${holdersLabel(phone)}.`
    : `Log in on ${rowTitle(phone)}. ${holdersLabel(phone)}.`;

export type ConfirmLine = { text: string; strong: boolean };

// The lines under "Log in on Front desk?" — only the ones that apply, the one
// that could change the decision first and in bold.
export function confirmLines(
  phone: DeskPhone,
  me: DeskPhones["me"] | null,
  phones: DeskPhone[]
): ConfirmLine[] {
  const lines: ConfirmLine[] = [];
  const ringsFor = phone.session?.name ?? ownerNames(phone)[0] ?? null;
  if (phone.on_call && ringsFor)
    lines.push({
      text: `${ringsFor} is on a call on this phone right now.`,
      strong: true
    });
  if (phone.session && !phone.mine)
    lines.push({
      text: `${phone.session.name} will be logged out of this phone.`,
      strong: false
    });
  if (!phone.own)
    for (const owner of phone.owners)
      lines.push({
        text: `${possessive(owner.name)} calls stop ringing here until you log out.`,
        strong: false
      });
  const ownPhone =
    me?.own_phone_id && me.own_phone_id !== phone.id
      ? (phones.find((candidate) => candidate.id === me.own_phone_id) ?? null)
      : null;
  if (ownPhone)
    lines.push({
      text: `Your own phone, ${ownPhone.name}, won't ring until you log out.`,
      strong: false
    });
  if (me?.session && me.session.device_id !== phone.id)
    lines.push({
      text: `You'll be logged out of ${me.session.device_name}.`,
      strong: false
    });
  if (phone.type === "dect_handset")
    lines.push({ text: DECT_HANDSET_NOTE, strong: false });
  // Logging in on your own phone starts no session: nothing to end overnight.
  if (!phone.own) lines.push({ text: UNTIL_NOTE, strong: false });
  return lines;
}

// The committing button says so when the person it logs out is on a call.
export const logInLabel = (phone: DeskPhone) =>
  phone.on_call ? LOG_IN_ANYWAY_LABEL : LOG_IN_LABEL;

// Where the user's calls ring once they log out: their own phone, or here.
export const ringsOnAfterLogOut = (
  me: DeskPhones["me"] | null,
  phones: DeskPhone[]
) => {
  const own = me?.own_phone_id
    ? phones.find((phone) => phone.id === me.own_phone_id)
    : null;
  return own ? own.name : THIS_COMPUTER_LABEL;
};

export function logOutBody(
  session: MyDeskPhoneSession,
  phone: DeskPhone | null,
  ringsOn: string
): string {
  const owners = phone ? ownerNames(phone) : [];
  const back =
    owners.length > 0
      ? `${possessiveNames(owners)} calls ring there again.`
      : `Nobody's calls ring on ${session.device_name} until someone logs in.`;
  return `${back} Your calls ring on ${ringsOn}.`;
}

// The walk-up prompt's one line, and its button.
export function walkUpLine(phone: DeskPhone): string {
  const owners = ownerNames(phone);
  if (phone.session)
    return `This desk's phone, ${phone.name}, has ${phone.session.name} logged in.`;
  if (owners.length > 0)
    return `This desk's phone, ${phone.name}, is ${possessiveNames(owners)}.`;
  return `This desk's phone, ${phone.name}, is free.`;
}
export const walkUpButtonLabel = (phone: DeskPhone) =>
  phone.session ? LOG_IN_AS_ME_LABEL : LOG_IN_LABEL;

// The Settings card's one-sentence status.
export function statusSentence(
  me: DeskPhones["me"],
  phones: DeskPhone[]
): string {
  if (me.session)
    return `You're logged in on ${me.session.device_name} until you log out or ${RESET_TIME_LABEL}.`;
  const own = me.own_phone_id
    ? phones.find((phone) => phone.id === me.own_phone_id)
    : null;
  if (own) return `Your calls ring on ${own.name}, your own phone.`;
  return "Your calls ring on this computer.";
}

// The history page's words for how a login started and ended.
export function historyEndLabel(row: DeskPhoneHistoryRow): string {
  if (row.is_open) return "Logged in now";
  switch (row.end_reason) {
    case "logged_out":
      return "Logged out";
    case "expired":
      return "Expired";
    case "replaced":
      return row.end_detail.by_name
        ? `Replaced by ${row.end_detail.by_name}`
        : "Replaced";
    case "moved":
      return row.end_detail.to_device_name
        ? `Moved to ${row.end_detail.to_device_name}`
        : "Moved to another phone";
    case "reset":
      return "Overnight reset";
    default:
      return "Ended";
  }
}

export function historyViaLabel(via: DeskPhoneHistoryRow["started_via"]) {
  switch (via) {
    case "suno_app":
      return "From Suno";
    case "provider":
      return "At the phone or portal";
    default:
      return "";
  }
}

export function durationLabel(seconds: number | null): string {
  if (seconds === null) return "";
  if (seconds < 60) return "under a minute";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}
